import React from 'react';
import { CircularProgress, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

import { LoaderOverlay, NoItemsStyled, StyledTableHeaderCell, StyledTableRow, TableWrapper } from './Table.styles';

export interface TableColumn<T> {
  renderCell: (item: T) => React.ReactNode;
  renderHeaderCell?: () => React.ReactNode;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  onHeaderClick?: () => void;
}

interface TableProps<T> {
  data: T[];
  isLoading?: boolean;
  onRowClick?: (item: T) => void;
  columns: TableColumn<T>[];
  noItemsErrorBody?: React.ReactNode;
}

const TableComponent = <T,>({ columns, data, onRowClick, noItemsErrorBody, isLoading }: TableProps<T>): JSX.Element => {
  const renderLoader = (): JSX.Element | null => {
    return isLoading ? (
      <LoaderOverlay>
        <CircularProgress data-qa-id="loader" />
      </LoaderOverlay>
    ) : null;
  };

  const onClickHeader = (col: TableColumn<T>) => col.onHeaderClick && col.onHeaderClick();
  const onClickRow = (row: T) => onRowClick && onRowClick(row);

  return (
    <>
      <TableWrapper>
        <TableHead>
          <TableRow>
            {columns.map((col, headIdx) => {
              return (
                <StyledTableHeaderCell
                  clickable={col.onHeaderClick != null}
                  align={col.align}
                  key={headIdx}
                  onClick={() => onClickHeader(col)}
                >
                  {col.renderHeaderCell ? col.renderHeaderCell() : null}
                </StyledTableHeaderCell>
              );
            })}
          </TableRow>
        </TableHead>
        {data.length === 0 ? null : (
          <TableBody>
            {data.map((item, rowIdx) => {
              return (
                <StyledTableRow key={rowIdx} clickable={onRowClick != null} onClick={() => onClickRow(item)}>
                  {columns.map((row, colIdx) => {
                    return (
                      <TableCell key={colIdx} align={row.align}>
                        {row.renderCell(item)}
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              );
            })}
            {renderLoader()}
          </TableBody>
        )}
      </TableWrapper>
      {data.length === 0 ? <NoItemsStyled>{noItemsErrorBody}</NoItemsStyled> : null}
    </>
  );
};

export default TableComponent;
